import { useEffect, useRef, useState } from 'react';
import { updateStudentProfile, sendParentInvite, assetUrl } from '../../services/api';
import { getRumaalColor } from '../../utils/colors';
import { invalidParentEmails, parentEmailsToText } from '../../utils/parentEmails';

const FIELD = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm transition-shadow duration-150 outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';

// Photos come off a parent's phone at several megabytes each, and roughly 300
// are coming. Resizing in the browser means what crosses the network is a
// thumbnail, and the server never has to hold a full-size image in memory.
const MAX_DIMENSION = 800;

function resizeToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('That file is not an image'));
      image.onload = () => {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const GENDERS = ['Girl', 'Boy', 'Other'];

const PORTAL_LABELS = {
  activated: { text: 'Signed in', className: 'bg-green-100 text-green-700' },
  invited: { text: 'Invited', className: 'bg-purple-100 text-purple-700' },
  'not-invited': { text: 'Not invited yet', className: 'bg-amber-100 text-amber-700' },
  'no-email': { text: 'No parent email', className: 'bg-gray-100 text-gray-600' },
};

export default function StudentEditor({ student, onClose, onSaved }) {
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [emails, setEmails] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [newPhoto, setNewPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!student) return;
    setDateOfBirth(student.dateOfBirth || '');
    setGender(student.gender || '');
    setEmails(parentEmailsToText(student.parentEmails));
    setPhotoPreview(student.photoUrl || '');
    setNewPhoto(null);
    setError('');
    setNotice('');
  }, [student]);

  if (!student) return null;

  const badEmails = invalidParentEmails(emails);

  // Age implied by what is currently typed, when it is outside the range an
  // academy plausibly teaches. Computed here rather than read off the student,
  // so the warning appears as the date is edited and not only after saving.
  const implausibleAge = (() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) return null;
    const born = new Date(`${dateOfBirth}T00:00:00Z`);
    if (Number.isNaN(born.getTime())) return null;
    const years = Math.floor((Date.now() - born.getTime()) / (365.2425 * 24 * 60 * 60 * 1000));
    return years < 3 || years > 80 ? years : null;
  })();

  const handlePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeToDataUrl(file);
      setNewPhoto(dataUrl);
      setPhotoPreview(dataUrl);
      setError('');
    } catch (photoError) {
      setError(photoError.message);
    }
  };

  const handleSave = async () => {
    if (badEmails.length) {
      setError(`Check this email address: ${badEmails.join(', ')}`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        dateOfBirth: dateOfBirth || null,
        gender: gender || null,
        parentEmails: emails,
      };
      if (newPhoto) payload.photo = newPhoto;

      const { data } = await updateStudentProfile(student.id, payload);
      setNewPhoto(null);
      setNotice('Saved');
      onSaved(data);
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Could not save these changes');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    setError('');
    try {
      const { data } = await sendParentInvite(student.id);
      setNotice(`Invitation sent to ${data.email}`);
      onSaved({ ...student, portalStatus: 'invited' });
    } catch (inviteError) {
      setError(inviteError.response?.data?.error || 'Could not send the invitation');
    } finally {
      setInviting(false);
    }
  };

  const portal = PORTAL_LABELS[student.portalStatus] || PORTAL_LABELS['no-email'];
  const savedEmails = student.parentEmails || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={saving ? undefined : onClose} />
      <div className="relative h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-md">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-900">{student.displayName}</h2>
            <p className="text-xs text-gray-500">
              {student.rosterEntries?.length
                ? student.rosterEntries.map((e) => `${e.classLabel}`).join(' · ')
                : 'Not in any class'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="flex items-center gap-4">
            <div
              className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
              // Rank ring as a box-shadow — set the colour directly rather than
              // through a Tailwind theme variable.
              style={{ boxShadow: `0 0 0 3px ${getRumaalColor(student.rumaalRank)}` }}
            >
              {photoPreview ? (
                <img src={assetUrl(photoPreview)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xl font-semibold text-gray-400">
                  {student.displayName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <button
                onClick={() => fileRef.current?.click()}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                {photoPreview ? 'Change photo' : 'Add photo'}
              </button>
              <p className="mt-1.5 text-xs text-gray-500">
                Shown to their parent and on the instructor iPad.
              </p>
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
            </div>
          </div>

          <div>
            <label className={LABEL}>Date of birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className={FIELD}
            />
            {implausibleAge !== null ? (
              // A mistyped year is the likeliest mistake here and would put a
              // child in the wrong competition division while looking perfectly
              // valid. Warn, but don't block — the academy knows its students.
              <p className="mt-1 text-xs text-amber-600">
                That makes them {implausibleAge} — check the year is right.
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                {student.ageDivision
                  ? `${student.age} years old · ${student.ageDivision}`
                  : 'Sets their competition age division and their birthday marker.'}
              </p>
            )}
          </div>

          <div>
            <label className={LABEL}>Gender</label>
            <div className="flex gap-2">
              {GENDERS.map((option) => (
                <button
                  key={option}
                  onClick={() => setGender(gender === option ? '' : option)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    gender === option
                      ? 'border-purple-600 bg-purple-600 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={LABEL}>Parent email</label>
            <input
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="parent@example.com"
              className={`${FIELD} ${badEmails.length ? 'border-red-300 bg-red-50/60' : ''}`}
            />
            {badEmails.length > 0 && (
              <p className="mt-1 text-xs text-red-600">Check this address: {badEmails.join(', ')}</p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${portal.className}`}>
                {portal.text}
              </span>
              {savedEmails.length > 0 && student.portalStatus !== 'activated' && (
                <button
                  onClick={handleInvite}
                  disabled={inviting}
                  className="rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {inviting ? 'Sending…' : student.portalStatus === 'invited' ? 'Send again' : 'Send invitation'}
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              The invitation signs them straight in — there is no password to set.
            </p>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {notice && !error && <p className="text-sm text-green-700">{notice}</p>}

          <div className="flex gap-2 border-t border-gray-200 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
